import { useMemo, useState, useRef, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import type { Task } from '@focus/shared';
import { Icon, type IconName } from './icon';
import { cn } from '@/lib/cn';
import { parseInlineTask, scoreFor } from '@focus/shared';
import { useUI } from '@/stores/ui';
import { scoreTone } from './app-colors';

type KindKey = 'all' | 'task' | 'event' | 'habit' | 'note';

function ClickLabel({
  value,
  options,
  onChange,
  title,
  width = 96,
  compact = false,
  icons,
}: {
  value: string;
  options: readonly string[];
  onChange: (v: string) => void;
  title?: string;
  width?: number;
  compact?: boolean;
  icons?: readonly IconName[];
}) {
  const next = () => onChange(options[(options.indexOf(value) + 1) % options.length]);
  if (compact && icons) {
    const idx = options.indexOf(value);
    const iconName = icons[idx] ?? icons[0];
    return (
      <button
        type="button"
        onClick={next}
        title={title ? `${title} (${value})` : value}
        className="h-7 w-7 inline-flex items-center justify-center bg-paper dark:bg-dark-paper border border-line dark:border-dark-line rounded-[7px] text-fg dark:text-dark-fg hover:bg-paperAlt dark:hover:bg-dark-paperAlt transition shrink-0"
      >
        <Icon name={iconName} size={14} />
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={next}
      title={title}
      style={{ width }}
      className="h-7 px-2.5 inline-flex items-center justify-center bg-paper dark:bg-dark-paper border border-line dark:border-dark-line rounded-[7px] text-[12.5px] font-normal text-fg dark:text-dark-fg hover:bg-paperAlt dark:hover:bg-dark-paperAlt transition shrink-0"
    >
      {value}
    </button>
  );
}

function TogglePill({
  on,
  onToggle,
  label,
  title,
  compact = false,
  icon,
}: {
  on: boolean;
  onToggle: () => void;
  label: string;
  title?: string;
  compact?: boolean;
  icon?: IconName;
}) {
  if (compact && icon) {
    return (
      <button
        type="button"
        onClick={onToggle}
        title={title ?? label}
        className={cn(
          'h-7 w-7 rounded-[7px] inline-flex items-center justify-center transition shrink-0',
          on
            ? 'bg-fg text-paper border border-fg dark:bg-dark-fg dark:text-dark-bg dark:border-dark-fg'
            : 'bg-paper text-fg border border-line hover:bg-paperAlt dark:bg-dark-paper dark:text-dark-fg dark:border-dark-line dark:hover:bg-dark-paperAlt',
        )}
      >
        <Icon name={icon} size={14} />
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={onToggle}
      title={title}
      className={cn(
        'h-7 px-3 rounded-[7px] text-[12.5px] font-normal transition shrink-0',
        on
          ? 'bg-fg text-paper border border-fg dark:bg-dark-fg dark:text-dark-bg dark:border-dark-fg'
          : 'bg-paper text-fg border border-line hover:bg-paperAlt dark:bg-dark-paper dark:text-dark-fg dark:border-dark-line dark:hover:bg-dark-paperAlt',
      )}
    >
      {label}
    </button>
  );
}

function StepMini({ dir, onClick }: { dir: 'up' | 'down'; onClick: (e: React.MouseEvent) => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-[14px] h-[9px] inline-flex items-center justify-center bg-paper dark:bg-dark-paper border border-line dark:border-dark-line rounded-[2px] text-fg dark:text-dark-fg p-0 hover:bg-paperAlt dark:hover:bg-dark-paperAlt transition"
    >
      <svg width="8" height="5" viewBox="0 0 8 5" fill="none">
        {dir === 'up' ? (
          <path d="M1 4L4 1L7 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        ) : (
          <path d="M1 1L4 4L7 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        )}
      </svg>
    </button>
  );
}

function GruppeDropdown({
  mode,
  setMode,
  scoreMin,
  setScoreMin,
  topN,
  setTopN,
  compact = false,
}: {
  mode: 'off' | 'minScore' | 'topN';
  setMode: (v: 'off' | 'minScore' | 'topN') => void;
  scoreMin: number;
  setScoreMin: (n: number) => void;
  topN: number;
  setTopN: (n: number) => void;
  compact?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const trigger = () => {
    if (mode === 'off') {
      return <span className="flex-1 text-center whitespace-nowrap">Alle</span>;
    }
    const prefix = mode === 'minScore' ? 'Score' : 'Top';
    const value = mode === 'minScore' ? scoreMin : topN;
    const setValue = mode === 'minScore' ? setScoreMin : setTopN;
    const min = mode === 'minScore' ? 0 : 1;
    const max = mode === 'minScore' ? 100 : 99;
    return (
      <span className="flex-1 inline-flex items-center justify-center gap-[5px] whitespace-nowrap">
        <span>{prefix}</span>
        <span className="min-w-[10px] text-center font-mono tabular-nums font-medium">{value}</span>
        <span className="inline-flex flex-col gap-[1px]" onClick={(e) => e.stopPropagation()}>
          <StepMini
            dir="up"
            onClick={(e) => {
              e.stopPropagation();
              const step = mode === 'minScore' ? 5 : 1;
              setValue(Math.min(max, value + step));
            }}
          />
          <StepMini
            dir="down"
            onClick={(e) => {
              e.stopPropagation();
              const step = mode === 'minScore' ? 5 : 1;
              setValue(Math.max(min, value - step));
            }}
          />
        </span>
      </span>
    );
  };

  const triggerTitle =
    mode === 'off' ? 'Filter: Alle' : mode === 'minScore' ? `Filter: Score ≥ ${scoreMin}` : `Filter: Top ${topN}`;

  return (
    <div ref={ref} className="relative shrink-0">
      {compact ? (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          title={triggerTitle}
          className={cn(
            'h-7 w-7 rounded-[7px] inline-flex items-center justify-center transition',
            mode !== 'off'
              ? 'bg-fg text-paper border border-fg dark:bg-dark-fg dark:text-dark-bg dark:border-dark-fg'
              : 'bg-paper text-fg border border-line hover:bg-paperAlt dark:bg-dark-paper dark:text-dark-fg dark:border-dark-line dark:hover:bg-dark-paperAlt',
          )}
        >
          <Icon name="gauge" size={14} />
        </button>
      ) : (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          title={triggerTitle}
          className="w-[96px] h-7 px-2 inline-flex items-center justify-center bg-paper dark:bg-dark-paper border border-line dark:border-dark-line rounded-[7px] text-[12.5px] font-normal text-fg dark:text-dark-fg whitespace-nowrap hover:bg-paperAlt dark:hover:bg-dark-paperAlt transition"
        >
          {trigger()}
        </button>
      )}
      {open && (
        <div className="absolute top-[calc(100%+4px)] right-0 w-[120px] bg-paper dark:bg-dark-paper border border-line dark:border-dark-line rounded-md shadow-pop p-1 z-30">
          {(['off', 'minScore', 'topN'] as const).map((k) => {
            const label = k === 'off' ? 'Alle' : k === 'minScore' ? 'Score' : 'Top';
            const active = mode === k;
            const value = k === 'minScore' ? scoreMin : k === 'topN' ? topN : null;
            return (
              <button
                key={k}
                type="button"
                onClick={() => {
                  setMode(k);
                  setOpen(false);
                }}
                className={cn(
                  'w-full flex items-center justify-between px-2.5 py-1.5 rounded-[6px] text-[12.5px] text-fg dark:text-dark-fg font-normal text-left transition',
                  active ? 'bg-paperAlt dark:bg-dark-paperAlt' : 'hover:bg-paperAlt dark:hover:bg-dark-paperAlt',
                )}
              >
                <span>{label}</span>
                {value !== null && (
                  <span className="font-mono tabular-nums opacity-70">{value}</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function Sidebar() {
  const qc = useQueryClient();
  const { data: tasks = [] } = useQuery({ queryKey: ['tasks'], queryFn: api.listTasks });
  const {
    openModal,
    setToast,
    sidebarWidth,
    setSidebarWidth,
    sort,
    setSort,
    tagOrder,
    setTagOrder,
    groupByTag,
    toggleGroupByTag,
    taskFilterMode,
    taskFilterMin,
    taskFilterTopN,
    setTaskFilterMode,
    setTaskFilterMin,
    setTaskFilterTopN,
    showDone,
    toggleShowDone,
    showChunksAll,
    toggleShowChunksAll,
  } = useUI();
  const sidebarCompact = sidebarWidth < 360;
  const [kind, setKind] = useState<KindKey>('all');
  const [groupInput, setGroupInput] = useState<Record<string, string>>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [q, setQ] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const [renaming, setRenaming] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [flatQuickValue, setFlatQuickValue] = useState('');
  const [hoveredTaskId, setHoveredTaskId] = useState<string | null>(null);

  const createTask = useMutation({
    mutationFn: async ({ title, tag }: { title: string; tag: string }) => {
      const parsed = parseInlineTask(title);
      return api.createTask({ ...parsed, tag: parsed.tag ?? tag });
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['tasks'] });
      setGroupInput((s) => ({ ...s, [vars.tag]: '' }));
      setToast({ msg: 'Task angelegt', tone: 'accent' });
      setTimeout(() => setToast(null), 1500);
    },
  });

  const toggleDone = useMutation({
    mutationFn: (id: string) => api.toggleTaskDone(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tasks'] }),
  });

  const toggleChunk = useMutation({
    mutationFn: async ({ taskId, chunkId }: { taskId: string; chunkId: string }) => {
      const current = tasks.find((t) => t.id === taskId);
      if (!current) return;
      const chunks = Array.isArray(current.chunks) ? current.chunks : [];
      const next = chunks.map((c) => (c.id === chunkId ? { ...c, done: !c.done } : c));
      await api.patchTask(taskId, { chunks: next } as any);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tasks'] }),
  });

  const addChunk = useMutation({
    mutationFn: async ({ taskId, title, est }: { taskId: string; title: string; est?: string }) => {
      const current = tasks.find((t) => t.id === taskId);
      if (!current) return;
      const chunks = Array.isArray(current.chunks) ? current.chunks : [];
      const id = `ch_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
      const next = [...chunks, { id, title, est: est?.trim() || '30m', done: false }];
      await api.patchTask(taskId, { chunks: next } as any);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tasks'] }),
  });

  const deleteTask = useMutation({
    mutationFn: (id: string) => api.deleteTask(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tasks'] }),
  });

  const retagMutation = useMutation({
    mutationFn: ({ from, to }: { from: string; to: string }) => api.retagTasks(from, to),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tasks'] }),
  });
  const deleteByTagMutation = useMutation({
    mutationFn: (tag: string) => api.deleteTasksByTag(tag),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tasks'] }),
  });

  const commitRename = useCallback(
    (fromTag: string) => {
      const to = renameValue.trim();
      setRenaming(null);
      setRenameValue('');
      if (!to || to === fromTag) return;
      retagMutation.mutate({ from: fromTag, to });
      const next = tagOrder.slice();
      const idx = next.indexOf(fromTag);
      if (idx >= 0) next[idx] = to;
      else next.push(to);
      setTagOrder(next);
      setToast({ msg: `Thema umbenannt: „${fromTag}" → „${to}"`, tone: 'accent' });
      setTimeout(() => setToast(null), 1500);
    },
    [renameValue, retagMutation, tagOrder, setTagOrder, setToast],
  );

  const onDeleteTheme = useCallback(
    (tag: string, mode: 'move' | 'all') => {
      setConfirmDelete(null);
      if (mode === 'move') {
        retagMutation.mutate({ from: tag, to: 'Inbox' });
        setToast({ msg: `Thema „${tag}" entfernt, Tasks nach Inbox verschoben`, tone: 'accent' });
      } else {
        deleteByTagMutation.mutate(tag);
        setToast({ msg: `Thema „${tag}" samt Tasks gelöscht`, tone: 'bad' });
      }
      const next = tagOrder.filter((t) => t !== tag);
      setTagOrder(next);
      setTimeout(() => setToast(null), 1800);
    },
    [retagMutation, deleteByTagMutation, tagOrder, setTagOrder, setToast],
  );

  const [pendingDeleteIds, setPendingDeleteIds] = useState<Set<string>>(() => new Set());
  const deleteTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const requestDelete = useCallback(
    (id: string, title: string) => {
      // If already pending, nothing to do.
      if (deleteTimers.current.has(id)) return;
      setPendingDeleteIds((s) => {
        const n = new Set(s);
        n.add(id);
        return n;
      });
      const timer = setTimeout(() => {
        deleteTimers.current.delete(id);
        setPendingDeleteIds((s) => {
          const n = new Set(s);
          n.delete(id);
          return n;
        });
        deleteTask.mutate(id);
      }, 6000);
      deleteTimers.current.set(id, timer);
      setToast({
        msg: `„${title}" gelöscht`,
        tone: 'bad',
        actionLabel: 'Rückgängig',
        onAction: () => {
          const t = deleteTimers.current.get(id);
          if (t) clearTimeout(t);
          deleteTimers.current.delete(id);
          setPendingDeleteIds((s) => {
            const n = new Set(s);
            n.delete(id);
            return n;
          });
        },
      });
      setTimeout(() => {
        // Auto-hide toast slightly before the commit; the user still has ~1s of safety
        // because the delete only fires at 6s. Give them the full toast duration.
        // We keep setToast-null out here; only clear if this particular toast is still
        // the one showing (avoid stomping newer toasts).
        const cur = useUI.getState().toast;
        if (cur && cur.onAction) useUI.getState().setToast(null);
      }, 5800);
    },
    [deleteTask, setToast],
  );

  const byKind = useMemo(() => {
    if (kind === 'all') return tasks;
    return tasks.filter((t) => (t.kind ?? 'task') === kind);
  }, [tasks, kind]);

  const openTasks = useMemo(
    () => byKind.filter((t) => !t.done && !pendingDeleteIds.has(t.id)),
    [byKind, pendingDeleteIds],
  );
  const doneTasks = useMemo(
    () => byKind.filter((t) => t.done && !pendingDeleteIds.has(t.id)),
    [byKind, pendingDeleteIds],
  );

  const filtered = useMemo(() => {
    const lc = q.toLowerCase();
    const base = !lc
      ? openTasks
      : openTasks.filter(
          (t) => t.title.toLowerCase().includes(lc) || (t.tag || '').toLowerCase().includes(lc),
        );
    if (taskFilterMode === 'minScore') {
      return base.filter((t) => (scoreFor(t) ?? -1) >= taskFilterMin);
    }
    if (taskFilterMode === 'topN') {
      const sortedByScore = [...base].sort(
        (a, b) => (scoreFor(b) ?? -1) - (scoreFor(a) ?? -1),
      );
      const topIds = new Set(sortedByScore.slice(0, taskFilterTopN).map((t) => t.id));
      return base.filter((t) => topIds.has(t.id));
    }
    return base;
  }, [openTasks, q, taskFilterMode, taskFilterMin, taskFilterTopN]);

  const grouped = useMemo(() => {
    const groups: Record<string, Task[]> = {};
    for (const t of filtered) {
      const g = t.tag || 'Inbox';
      if (!groups[g]) groups[g] = [];
      groups[g].push(t);
    }
    const entries = Object.entries(groups);
    for (const [, items] of entries) {
      if (sort === 'score') items.sort((a, b) => (scoreFor(b) ?? -1) - (scoreFor(a) ?? -1));
      else if (sort === 'due') items.sort((a, b) => (a.urgDays ?? 999) - (b.urgDays ?? 999));
      else items.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }
    const rank = new Map<string, number>();
    tagOrder.forEach((t, i) => rank.set(t, i));
    entries.sort((a, b) => {
      const ra = rank.get(a[0]);
      const rb = rank.get(b[0]);
      if (ra != null && rb != null) return ra - rb;
      if (ra != null) return -1;
      if (rb != null) return 1;
      if (sort === 'manual') {
        const minA = Math.min(...a[1].map((t) => t.order ?? Infinity));
        const minB = Math.min(...b[1].map((t) => t.order ?? Infinity));
        return minA - minB;
      }
      const maxA = Math.max(...a[1].map((t) => scoreFor(t) ?? -1));
      const maxB = Math.max(...b[1].map((t) => scoreFor(t) ?? -1));
      return maxB - maxA;
    });
    return entries;
  }, [filtered, sort, tagOrder]);

  const flatTasks = useMemo(() => {
    const arr = [...filtered];
    if (sort === 'score') arr.sort((a, b) => (scoreFor(b) ?? -1) - (scoreFor(a) ?? -1));
    else if (sort === 'due') arr.sort((a, b) => (a.urgDays ?? 999) - (b.urgDays ?? 999));
    else arr.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    return arr;
  }, [filtered, sort]);

  const dragStartX = useRef<{ x: number; startW: number } | null>(null);
  const onResizeStart = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      dragStartX.current = { x: e.clientX, startW: sidebarWidth };
      const move = (ev: PointerEvent) => {
        if (!dragStartX.current) return;
        const dx = ev.clientX - dragStartX.current.x;
        setSidebarWidth(dragStartX.current.startW + dx);
      };
      const up = () => {
        dragStartX.current = null;
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
      };
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
    },
    [sidebarWidth, setSidebarWidth],
  );

  const kinds: { k: KindKey; l: string }[] = [
    { k: 'all', l: 'Alle' },
    { k: 'task', l: 'Tasks' },
    { k: 'event', l: 'Termine' },
    { k: 'habit', l: 'Routinen' },
    { k: 'note', l: 'Notizen' },
  ];

  return (
    <aside
      className="shrink-0 bg-bg dark:bg-dark-bg border-r border-line dark:border-dark-line flex flex-col relative fc-sidebar"
      style={{ width: sidebarWidth }}
    >
      {/* V2 Header — tabs, search, filter row. */}
      <div className="px-3 pt-3 pb-2 flex flex-col gap-3 border-b border-line dark:border-dark-line">
        <div className="flex gap-[18px] flex-wrap">
          {kinds.map((tp) => {
            const active = kind === tp.k;
            return (
              <button
                key={tp.k}
                onClick={() => setKind(tp.k)}
                className={cn(
                  'relative py-1 text-[12.5px] transition',
                  active
                    ? 'text-fg dark:text-dark-fg font-semibold after:absolute after:left-0 after:right-0 after:-bottom-[3px] after:h-[1.5px] after:bg-fg dark:after:bg-dark-fg after:rounded-[1px]'
                    : 'text-fg dark:text-dark-fg font-normal hover:opacity-70',
                )}
              >
                {tp.l}
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-2 h-[30px] px-2.5 bg-paper dark:bg-dark-paper border border-line dark:border-dark-line rounded-md">
          <Icon name="search" size={13} className="text-fg dark:text-dark-fg opacity-70 shrink-0" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Suchen"
            className="flex-1 bg-transparent border-none outline-none font-normal text-[12.5px] text-fg dark:text-dark-fg placeholder:text-fg placeholder:dark:text-dark-fg placeholder:opacity-60 input-bare"
          />
        </div>

        <div className="flex items-center gap-[6px] flex-nowrap">
          <ClickLabel
            value={sort === 'score' ? 'Score' : sort === 'due' ? 'Deadline' : 'Manuell'}
            options={['Score', 'Deadline', 'Manuell'] as const}
            onChange={(v) => setSort(v === 'Score' ? 'score' : v === 'Deadline' ? 'due' : 'manual')}
            title="Sortierung wechseln"
            compact={sidebarCompact}
            icons={['chart', 'clock', 'pencil'] as const}
          />
          <TogglePill
            on={showChunksAll}
            onToggle={toggleShowChunksAll}
            label="Chunks"
            title="Chunks an/aus"
            compact={sidebarCompact}
            icon="check"
          />
          <TogglePill
            on={groupByTag}
            onToggle={toggleGroupByTag}
            label="Themen"
            title="Themen-Gruppierung an/aus"
            compact={sidebarCompact}
            icon="layers"
          />
          <div className="flex-1 min-w-0" />
          <button
            type="button"
            onClick={() => openModal('export')}
            title="Liste exportieren / kopieren"
            className="h-7 w-7 inline-flex items-center justify-center bg-paper dark:bg-dark-paper border border-line dark:border-dark-line rounded-[7px] text-fg dark:text-dark-fg hover:bg-paperAlt dark:hover:bg-dark-paperAlt transition shrink-0"
          >
            <Icon name="download" size={13} />
          </button>
          <GruppeDropdown
            mode={taskFilterMode}
            setMode={setTaskFilterMode}
            scoreMin={taskFilterMin}
            setScoreMin={setTaskFilterMin}
            topN={taskFilterTopN}
            setTopN={setTaskFilterTopN}
            compact={sidebarCompact}
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto scroll-thin px-2 pt-2 pr-3 pb-6">
        {groupByTag ? (
          grouped.map(([tag, items]) => {
            const collapsed = !!collapsedGroups[tag];
            const value = groupInput[tag] ?? '';
            const isRenaming = renaming === tag;
            const isConfirming = confirmDelete === tag;
            return (
              <div key={tag} className="mb-2 group/grp relative">
                {isConfirming ? (
                  <div className="px-2 py-2 rounded-md bg-bad/10 border border-bad/30 space-y-1.5">
                    <div className="text-[11.5px] font-semibold text-fg dark:text-dark-fg">
                      Thema „{tag}" löschen?
                    </div>
                    <div className="flex flex-col gap-1">
                      <button
                        onClick={() => onDeleteTheme(tag, 'move')}
                        className="text-[11px] px-2 py-1 rounded border border-line dark:border-dark-line bg-paper dark:bg-dark-paper hover:border-accent text-left"
                      >
                        Nur Thema entfernen, Tasks in Inbox verschieben
                      </button>
                      <button
                        onClick={() => onDeleteTheme(tag, 'all')}
                        className="text-[11px] px-2 py-1 rounded border border-bad/40 bg-bad/15 text-bad hover:bg-bad/25 text-left"
                      >
                        Thema + alle {items.length} Task{items.length === 1 ? '' : 's'} löschen
                      </button>
                      <button
                        onClick={() => setConfirmDelete(null)}
                        className="text-[10.5px] text-fg dark:text-dark-fg hover:text-fg dark:hover:text-dark-fg self-start"
                      >
                        Abbrechen
                      </button>
                    </div>
                  </div>
                ) : (
                  <GroupHeader
                    tag={tag}
                    collapsed={collapsed}
                    isRenaming={isRenaming}
                    renameValue={renameValue}
                    onToggleCollapsed={() =>
                      setCollapsedGroups((s) => ({ ...s, [tag]: !s[tag] }))
                    }
                    onStartRename={() => {
                      setRenameValue(tag);
                      setRenaming(tag);
                    }}
                    onRenameChange={setRenameValue}
                    onRenameCommit={() => commitRename(tag)}
                    onRenameCancel={() => {
                      setRenaming(null);
                      setRenameValue('');
                    }}
                    onRequestDelete={() => setConfirmDelete(tag)}
                  />
                )}
                {!collapsed && !isConfirming && (
                  <div
                    className="space-y-[5px]"
                    onMouseLeave={() => setHoveredTaskId(null)}
                  >
                    {items.map((t) => (
                      <TodoCard
                        key={t.id}
                        task={t}
                        expanded={!!expanded[t.id]}
                        forceChunksVisible={showChunksAll}
                        showHoverActions={hoveredTaskId === t.id}
                        onHoverStart={() => setHoveredTaskId(t.id)}
                        onExpand={() => setExpanded((e) => ({ ...e, [t.id]: !e[t.id] }))}
                        onToggle={() => toggleDone.mutate(t.id)}
                        onOpen={() => openModal('taskDetail', t.id)}
                        onDelete={() => requestDelete(t.id, t.title)}
                        onToggleChunk={(chunkId) => toggleChunk.mutate({ taskId: t.id, chunkId })}
                        onAddChunk={(title, est) => addChunk.mutate({ taskId: t.id, title, est })}
                      />
                    ))}
                    <GroupInput
                      tag={tag}
                      value={value}
                      onChange={(v) => setGroupInput((s) => ({ ...s, [tag]: v }))}
                      onSubmit={() => {
                        if (value.trim()) createTask.mutate({ title: value.trim(), tag });
                      }}
                    />
                  </div>
                )}
              </div>
            );
          })
        ) : (
          <div
            className="space-y-[5px]"
            onMouseLeave={() => setHoveredTaskId(null)}
          >
            <FlatQuickInput
              value={flatQuickValue}
              onChange={setFlatQuickValue}
              onSubmit={() => {
                const v = flatQuickValue.trim();
                if (!v) return;
                createTask.mutate({ title: v, tag: 'Inbox' });
                setFlatQuickValue('');
              }}
            />
            {flatTasks.map((t) => (
              <TodoCard
                key={t.id}
                task={t}
                expanded={!!expanded[t.id]}
                forceChunksVisible={showChunksAll}
                showHoverActions={hoveredTaskId === t.id}
                onHoverStart={() => setHoveredTaskId(t.id)}
                onExpand={() => setExpanded((e) => ({ ...e, [t.id]: !e[t.id] }))}
                onToggle={() => toggleDone.mutate(t.id)}
                onOpen={() => openModal('taskDetail', t.id)}
                onDelete={() => requestDelete(t.id, t.title)}
                onToggleChunk={(chunkId) => toggleChunk.mutate({ taskId: t.id, chunkId })}
                onAddChunk={(title, est) => addChunk.mutate({ taskId: t.id, title, est })}
              />
            ))}
          </div>
        )}

        {groupByTag && grouped.length === 0 && (
          <div className="p-6 text-center text-[12px] text-fg dark:text-dark-fg">
            Keine Tasks.<br />Leg eine Kategorie an und füge Tasks hinzu.
          </div>
        )}
        {!groupByTag && flatTasks.length === 0 && (
          <div className="p-6 text-center text-[12px] text-fg dark:text-dark-fg">
            Keine offenen Tasks.
          </div>
        )}

        {doneTasks.length > 0 && (
          <div className="mt-10 pt-5 border-t border-line dark:border-dark-line">
            <button
              onClick={toggleShowDone}
              className="w-full px-2 pb-1.5 flex items-center gap-1.5 text-[13px] font-semibold tracking-[-0.1px] text-fg dark:text-dark-fg transition"
              title={showDone ? 'Erledigt ausblenden' : 'Erledigt einblenden'}
            >
              <Icon name={showDone ? 'chevronDown' : 'chevronRight'} size={10} />
              <span>Erledigt</span>
              <span className="font-mono tabular-nums opacity-70">· {doneTasks.length}</span>
            </button>
            {showDone && (
              <div className="space-y-[5px]">
                {doneTasks.slice(0, 10).map((t) => (
                  <div
                    key={t.id}
                    className="flex items-start gap-2 px-2 py-[6px] rounded opacity-60 hover:opacity-100 task-done-slide"
                  >
                    <button
                      onClick={() => toggleDone.mutate(t.id)}
                      className="w-[14px] h-[14px] shrink-0 mt-[2px] rounded-[4px] bg-accent border border-accent grid place-items-center"
                      aria-label="Erledigt rückgängig"
                    >
                      <Icon name="check" size={10} className="text-white" />
                    </button>
                    <div className="text-[14px] line-through flex-1 min-w-0 leading-snug break-words text-fg dark:text-dark-fg">{t.title}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Resize handle, 5px wide with visible 1px grab area */}
      <div
        onPointerDown={onResizeStart}
        className="absolute top-0 right-0 w-1.5 h-full cursor-col-resize hover:bg-accent/40 active:bg-accent/60 transition z-20"
        aria-label="Sidebar-Breite"
      />
    </aside>
  );
}

function GroupInput({
  tag,
  value,
  onChange,
  onSubmit,
}: {
  tag: string;
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
}) {
  const hasValue = value.length > 0;
  return (
    <div
      className={cn(
        'overflow-hidden transition-all duration-150',
        hasValue
          ? 'max-h-[40px] opacity-100'
          : 'max-h-0 opacity-0 group-hover/grp:max-h-[40px] group-hover/grp:opacity-100 focus-within:max-h-[40px] focus-within:opacity-100',
      )}
    >
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onSubmit();
          if (e.key === 'Escape') onChange('');
        }}
        placeholder={`+ Task in ${tag} …`}
        className="w-full mt-1 px-3 py-1.5 text-[12px] rounded-md border border-dashed border-line dark:border-dark-line bg-transparent input-bare focus:border-accent focus:bg-paper dark:focus:bg-dark-paper"
      />
    </div>
  );
}

function FlatQuickInput({
  value,
  onChange,
  onSubmit,
}: {
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
}) {
  return (
    <div className="mb-1.5">
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onSubmit();
          if (e.key === 'Escape') onChange('');
        }}
        placeholder="+ Neuer Task in Inbox …"
        className="w-full px-3 py-1.5 text-[12px] rounded-md border border-dashed border-line dark:border-dark-line bg-transparent input-bare focus:border-accent focus:bg-paper dark:focus:bg-dark-paper"
      />
    </div>
  );
}

function GroupHeader({
  tag,
  collapsed,
  isRenaming,
  renameValue,
  onToggleCollapsed,
  onStartRename,
  onRenameChange,
  onRenameCommit,
  onRenameCancel,
  onRequestDelete,
}: {
  tag: string;
  collapsed: boolean;
  isRenaming: boolean;
  renameValue: string;
  onToggleCollapsed: () => void;
  onStartRename: () => void;
  onRenameChange: (v: string) => void;
  onRenameCommit: () => void;
  onRenameCancel: () => void;
  onRequestDelete: () => void;
}) {
  const { attributes, listeners, setNodeRef: setDragRef, isDragging } = useDraggable({
    id: `group:${tag}`,
    data: { type: 'group', tag },
    disabled: isRenaming,
  });
  const { setNodeRef: setBeforeRef, isOver: isOverBefore, active } = useDroppable({
    id: `group-before:${tag}`,
    data: { type: 'group-target', tag, placement: 'before' },
  });
  const { setNodeRef: setAfterRef, isOver: isOverAfter } = useDroppable({
    id: `group-after:${tag}`,
    data: { type: 'group-target', tag, placement: 'after' },
  });
  const activeData = active?.data?.current as any;
  const isActiveGroup = activeData?.type === 'group' && activeData?.tag !== tag;
  const showBefore = isOverBefore && isActiveGroup;
  const showAfter = isOverAfter && isActiveGroup;

  return (
    <div className={cn('relative', isDragging && 'opacity-40')}>
      {showBefore && (
        <div className="absolute left-0 right-0 -top-[1px] h-[2px] bg-accent rounded-full pointer-events-none z-30" />
      )}
      {showAfter && (
        <div className="absolute left-0 right-0 -bottom-[1px] h-[2px] bg-accent rounded-full pointer-events-none z-30" />
      )}
      <div ref={setBeforeRef} className="absolute left-0 right-0 top-0 h-1/2 pointer-events-none z-10" aria-hidden />
      <div ref={setAfterRef} className="absolute left-0 right-0 bottom-0 h-1/2 pointer-events-none z-10" aria-hidden />
      {isRenaming ? (
        <div className="w-full px-1.5 py-1 flex items-center gap-1.5">
          <Icon name={collapsed ? 'chevronRight' : 'chevronDown'} size={10} className="opacity-50 shrink-0" />
          <input
            autoFocus
            value={renameValue}
            onChange={(e) => onRenameChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                onRenameCommit();
              } else if (e.key === 'Escape') {
                e.preventDefault();
                onRenameCancel();
              }
            }}
            onBlur={onRenameCancel}
            className="flex-1 min-w-0 text-[12px] font-semibold tracking-[-0.1px] text-fg dark:text-dark-fg bg-paper dark:bg-dark-paper border border-accent rounded px-1.5 py-[2px] input-bare"
          />
        </div>
      ) : (
        <div
          ref={setDragRef}
          {...attributes}
          {...listeners}
          onClick={onToggleCollapsed}
          className="w-full px-1.5 py-1 flex items-center gap-1.5 text-[12px] font-semibold tracking-[-0.1px] text-fg dark:text-dark-fg hover:opacity-80 transition cursor-grab active:cursor-grabbing select-none"
        >
          <Icon name={collapsed ? 'chevronRight' : 'chevronDown'} size={10} />
          <span className="truncate flex-1 text-left">{tag}</span>
          <div className="flex items-center gap-0.5 opacity-0 group-hover/grp:opacity-100 transition-opacity">
            <button
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                onStartRename();
              }}
              className="w-[18px] h-[18px] grid place-items-center rounded hover:bg-paperAlt dark:hover:bg-dark-paperAlt text-fg dark:text-dark-fg hover:text-fg dark:hover:text-dark-fg"
              aria-label="Thema umbenennen"
              title="Umbenennen"
            >
              <Icon name="pencil" size={11} />
            </button>
            <button
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                onRequestDelete();
              }}
              className="w-[18px] h-[18px] grid place-items-center rounded hover:bg-bad/15 hover:text-bad text-fg dark:text-dark-fg"
              aria-label="Thema löschen"
              title="Löschen"
            >
              <Icon name="x" size={12} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ScoreBadge({ score }: { score: number | null }) {
  if (score == null) {
    return (
      <span className="inline-flex items-center justify-center w-[22px] h-[16px] rounded-full text-[10.5px] font-mono text-fg dark:text-dark-fg opacity-50">
        –
      </span>
    );
  }
  const clamped = Math.max(0, Math.min(100, Math.round(score)));
  const t = scoreTone(clamped);
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-[1px] rounded-full text-[10.5px] font-semibold font-mono tracking-[0.1px]"
      style={{ background: t.bg, color: t.fg }}
    >
      <span className="w-[5px] h-[5px] rounded-full" style={{ background: t.dot }} />
      {clamped}
    </span>
  );
}

function TodoCard({
  task,
  expanded,
  forceChunksVisible = false,
  showHoverActions = false,
  onHoverStart,
  onExpand,
  onToggle,
  onOpen,
  onDelete,
  onToggleChunk,
  onAddChunk,
}: {
  task: Task;
  expanded: boolean;
  forceChunksVisible?: boolean;
  showHoverActions?: boolean;
  onHoverStart?: () => void;
  onExpand: () => void;
  onToggle: () => void;
  onOpen: () => void;
  onDelete: () => void;
  onToggleChunk: (chunkId: string) => void;
  onAddChunk: (title: string, est?: string) => void;
}) {
  const [newChunk, setNewChunk] = useState('');
  const newChunkRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (expanded) requestAnimationFrame(() => newChunkRef.current?.focus());
  }, [expanded]);
  const { attributes, listeners, setNodeRef: setDragRef, isDragging } = useDraggable({
    id: `task:${task.id}`,
    data: { type: 'task', task },
  });
  const { setNodeRef: setBeforeRef, isOver: isOverBefore, active } = useDroppable({
    id: `task-before:${task.id}`,
    data: { type: 'task-target', taskId: task.id, placement: 'before' },
  });
  const { setNodeRef: setAfterRef, isOver: isOverAfter } = useDroppable({
    id: `task-after:${task.id}`,
    data: { type: 'task-target', taskId: task.id, placement: 'after' },
  });
  const activeData = active?.data?.current as any;
  const isActiveTask = activeData?.type === 'task' && activeData?.task?.id !== task.id;
  const isActiveChunk =
    activeData?.type === 'chunk' && activeData?.sourceTaskId !== task.id;
  const showBefore = isOverBefore && (isActiveTask || isActiveChunk);
  const showAfter = isOverAfter && (isActiveTask || isActiveChunk);
  const isChunkTargetCandidate = isActiveChunk && (isOverBefore || isOverAfter);
  const score = scoreFor(task);
  const hasChunks = Array.isArray(task.chunks) && task.chunks.length > 0;
  const [justDone, setJustDone] = useState(false);

  const onCheckClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!task.done) {
      setJustDone(true);
      setTimeout(() => setJustDone(false), 540);
      // Delay the actual toggle so the wave animation plays before the row moves
      // to the hidden "Erledigt" section on re-render.
      setTimeout(() => onToggle(), 380);
    } else {
      onToggle();
    }
  };

  const onRowClick = (e: React.MouseEvent) => {
    // If the pointer registered a drag, dnd-kit sets isDragging. Don't open in that case.
    if (isDragging) return;
    e.stopPropagation();
    onOpen();
  };

  return (
    <div
      onMouseEnter={onHoverStart}
      className={cn(
        'rounded-md group/todo relative',
        isDragging && 'opacity-40',
        isChunkTargetCandidate && 'ring-1 ring-accent/60 ring-offset-0 rounded-md',
      )}
    >
      {showBefore && (
        <div className="absolute left-0 right-0 -top-[1px] h-[2px] bg-accent rounded-full pointer-events-none z-30" />
      )}
      {showAfter && (
        <div className="absolute left-0 right-0 -bottom-[1px] h-[2px] bg-accent rounded-full pointer-events-none z-30" />
      )}
      {/* Invisible half-height drop zones for precise before/after placement. */}
      <div ref={setBeforeRef} className="absolute left-0 right-0 top-0 h-1/2 pointer-events-none z-10" aria-hidden />
      <div ref={setAfterRef} className="absolute left-0 right-0 bottom-0 h-1/2 pointer-events-none z-10" aria-hidden />
      <div
        ref={setDragRef}
        {...attributes}
        {...listeners}
        onClick={onRowClick}
        className={cn(
          'relative flex items-center gap-2 pl-2 pr-2 py-[10px] rounded-[6px] min-w-0',
          'border border-transparent bg-transparent',
          'hover:bg-paperAlt dark:hover:bg-dark-paperAlt hover:border-line dark:hover:border-dark-line',
          'cursor-grab active:cursor-grabbing select-none transition',
          task.active && 'bg-accentSoft border-accentLine hover:bg-accentSoft',
          justDone && 'task-done-pop',
        )}
      >
        <button
          onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
          onClick={onCheckClick}
          className={cn(
            'relative shrink-0 w-[15px] h-[15px] rounded-[4px] border grid place-items-center transition-all duration-200',
            'before:absolute before:inset-[-6px] before:content-[""]',
            task.done
              ? 'bg-accent border-accent scale-100'
              : 'border-lineStrong dark:border-dark-lineStrong hover:border-accent hover:scale-110',
            justDone && 'check-pop',
          )}
          aria-label={task.done ? 'Erledigt rückgängig' : 'Als erledigt markieren'}
        >
          {task.done && <Icon name="check" size={10} className="text-white" />}
        </button>

        <div className="flex-1 min-w-0">
          <div className="text-[14.5px] font-normal leading-snug break-words tracking-[-0.1px] text-fg dark:text-dark-fg">
            {task.title}
          </div>
        </div>

        <div className="flex items-center gap-1.5 shrink-0">
          <ScoreBadge score={score} />
          <button
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className={cn(
              'transition-opacity w-[18px] h-[18px] grid place-items-center rounded hover:bg-bad/15 hover:text-bad text-fg dark:text-dark-fg',
              showHoverActions ? 'opacity-100' : 'opacity-0',
            )}
            aria-label="Task löschen"
            title="Löschen"
          >
            <Icon name="x" size={12} />
          </button>
        </div>
      </div>

      {/* Hover action bar, always rendered but hidden unless hovered or expanded.
          Visibility tracked via state in Sidebar so it persists when the cursor moves
          into the GroupInput below the list (CSS group-hover/todo would clear there). */}
      <div
        className={cn(
          'overflow-hidden transition-all duration-150',
          expanded || showHoverActions ? 'max-h-[36px] opacity-100' : 'max-h-0 opacity-0',
        )}
      >
        <div className="pl-7 pr-2 pt-0.5 pb-1 flex items-center gap-1">
          <button
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              onExpand();
            }}
            className="text-[11px] text-fg dark:text-dark-fg flex items-center gap-1 px-1.5 py-[2px] rounded hover:bg-paper dark:hover:bg-dark-paper transition"
          >
            <Icon name={expanded ? 'chevronDown' : 'plus'} size={10} />
            <span>{hasChunks ? `Chunks · ${task.chunks!.length}` : 'Chunk +'}</span>
          </button>
          <button
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              onOpen();
            }}
            className="text-[11px] text-fg dark:text-dark-fg flex items-center gap-1 px-1.5 py-[2px] rounded hover:bg-paper dark:hover:bg-dark-paper transition"
          >
            <Icon name="pencil" size={10} />
            <span>Details</span>
          </button>
        </div>
      </div>

      {/* Chunks list when expanded per-task OR globally toggled on. Input only when per-task. */}
      {(expanded || (forceChunksVisible && hasChunks)) && (
        <div className="pl-7 pr-2 pb-1 space-y-0.5">
          {hasChunks &&
            task.chunks!.map((c) => (
              <ChunkRow
                key={c.id}
                taskId={task.id}
                chunk={c}
                onToggle={() => onToggleChunk(c.id)}
              />
            ))}
          {expanded && (
          <input
            ref={newChunkRef}
            value={newChunk}
            onChange={(e) => setNewChunk(e.target.value)}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                const v = newChunk.trim();
                if (!v) return;
                onAddChunk(v);
                setNewChunk('');
              } else if (e.key === 'Escape') {
                e.preventDefault();
                setNewChunk('');
                (e.currentTarget as HTMLInputElement).blur();
              }
            }}
            placeholder="+ Neuer Chunk, Enter zum Hinzufügen"
            className="w-full mt-0.5 px-2 py-1 text-[12px] rounded-md border border-dashed border-line dark:border-dark-line bg-transparent input-bare focus:border-accent focus:bg-paper dark:focus:bg-dark-paper"
          />
          )}
        </div>
      )}
    </div>
  );
}

function ChunkRow({
  taskId,
  chunk,
  onToggle,
}: {
  taskId: string;
  chunk: { id: string; title: string; est: string; done: boolean; active?: boolean };
  onToggle: () => void;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `chunk:${taskId}:${chunk.id}`,
    data: { type: 'chunk', sourceTaskId: taskId, chunkId: chunk.id, chunk },
  });
  const [justDone, setJustDone] = useState(false);
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      className={cn(
        'relative flex items-center gap-2 py-[7px] text-[14px] text-fg dark:text-dark-fg cursor-grab active:cursor-grabbing select-none rounded-[4px] -mx-1 px-1 hover:bg-paperAlt dark:hover:bg-dark-paperAlt transition',
        isDragging && 'opacity-40',
        justDone && 'task-done-pop',
      )}
    >
      <button
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          if (!chunk.done) {
            setJustDone(true);
            setTimeout(() => setJustDone(false), 540);
            setTimeout(() => onToggle(), 380);
          } else {
            onToggle();
          }
        }}
        className={cn(
          'shrink-0 w-[14px] h-[14px] rounded-[3px] border grid place-items-center transition-all duration-150',
          chunk.done
            ? 'bg-accent border-accent'
            : chunk.active
              ? 'border-accent ring-1 ring-accent/30'
              : 'border-lineStrong dark:border-dark-lineStrong hover:border-accent hover:scale-110',
          justDone && 'check-pop',
        )}
        aria-label={chunk.done ? 'Chunk rückgängig' : 'Chunk abhaken'}
      >
        {chunk.done && <Icon name="check" size={9} className="text-white" />}
      </button>
      <span
        className={cn(
          'flex-1 min-w-0 leading-snug break-words text-fg dark:text-dark-fg',
          chunk.done && 'line-through opacity-60',
        )}
      >
        {chunk.title}
      </span>
    </div>
  );
}
